import {
  rrcProfileId,
  rrcProfileKey,
  type RrcProfileId,
  type RrcProfileKey,
} from '../rrc-profile-query';
import { otsBaseApi } from '../../ots-api';
import { RrcCompanyProfile } from '../../../model/rrc/rrc-company-profile';

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: [
    'RrcCompanyProfiles',
    'ObjectRrcCompanyProfiles',
    'KeyRrcCompanyProfiles',
    'CurrentRrcCompanyProfile',
    'ObjectObjectChangeLogs',
  ],
});

export const rrcCompanyProfileApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentRrcCompanyProfilesByCompany: builder.query<
      RrcCompanyProfile[],
      string
    >({
      query: (arg) => ({
        url: '/compliance/rrc/companies/current',
        params: { companyId: arg },
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => [
        { type: 'RrcCompanyProfiles', id: 'LIST' },
        { type: 'ObjectRrcCompanyProfiles', id: arg },
        ...(result ?? []).map(({ id }) => ({
          type: 'RrcCompanyProfiles' as const,
          id,
        })),
      ],
    }),
    getRrcCompanyProfilesByCompany: builder.query<RrcCompanyProfile[], string>({
      query: (arg) => ({
        url: '/compliance/rrc/companies',
        params: { companyId: arg },
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => [
        { type: 'RrcCompanyProfiles', id: 'LIST' },
        { type: 'ObjectRrcCompanyProfiles', id: arg },
        ...(result ?? []).map(({ id }) => ({
          type: 'RrcCompanyProfiles' as const,
          id,
        })),
      ],
    }),
    getRrcCompanyProfilesByKey: builder.query<
      RrcCompanyProfile[],
      RrcProfileKey
    >({
      query: (arg) => ({
        url: `/compliance/rrc/companies/by-key/${encodeURIComponent(
          rrcProfileKey(arg).profileKey
        )}/versions`,
        params: { tenantCode: rrcProfileKey(arg).tenantCode },
      }),
      serializeQueryArgs: ({ queryArgs }) => rrcProfileKey(queryArgs),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => [
        { type: 'KeyRrcCompanyProfiles', id: rrcProfileKey(arg).profileKey },
        ...(result ?? []).map(({ id }) => ({
          type: 'RrcCompanyProfiles' as const,
          id,
        })),
      ],
    }),
    getRrcCompanyProfile: builder.query<RrcCompanyProfile, RrcProfileId>({
      query: (arg) => ({
        url: `/compliance/rrc/companies/${encodeURIComponent(
          rrcProfileId(arg).id
        )}`,
        params: { tenantCode: rrcProfileId(arg).tenantCode },
      }),
      serializeQueryArgs: ({ queryArgs }) => rrcProfileId(queryArgs),
      providesTags: (result, error, arg) => [
        { type: 'RrcCompanyProfiles', id: rrcProfileId(arg).id },
      ],
    }),
    getCurrentRrcCompanyProfileByKey: builder.query<
      RrcCompanyProfile,
      RrcProfileKey
    >({
      query: (arg) => ({
        url: `/compliance/rrc/companies/by-key/${encodeURIComponent(
          rrcProfileKey(arg).profileKey
        )}`,
        params: { tenantCode: rrcProfileKey(arg).tenantCode },
      }),
      serializeQueryArgs: ({ queryArgs }) => rrcProfileKey(queryArgs),
      providesTags: (result, error, arg) => [
        { type: 'CurrentRrcCompanyProfile', id: rrcProfileKey(arg).profileKey },
        { type: 'KeyRrcCompanyProfiles', id: rrcProfileKey(arg).profileKey },
        ...(result
          ? [{ type: 'RrcCompanyProfiles' as const, id: result.id }]
          : []),
      ],
    }),
    createRrcCompanyProfile: builder.mutation<
      RrcCompanyProfile,
      Partial<RrcCompanyProfile>
    >({
      query: (arg) => ({
        url: '/compliance/rrc/companies',
        method: 'POST',
        body: arg,
      }),
      invalidatesTags: (result, error, args) => {
        if (result) {
          return [
            { type: 'RrcCompanyProfiles', id: 'LIST' },
            { type: 'KeyRrcCompanyProfiles', id: result.profileKey },
            { type: 'ObjectRrcCompanyProfiles', id: result.companyId },
            { type: 'CurrentRrcCompanyProfile' },
          ];
        } else {
          return [
            { type: 'RrcCompanyProfiles', id: 'LIST' },
            { type: 'KeyRrcCompanyProfiles' },
            { type: 'ObjectRrcCompanyProfiles' },
            { type: 'CurrentRrcCompanyProfile' },
          ];
        }
      },
    }),
    updateRrcCompanyProfile: builder.mutation<
      RrcCompanyProfile,
      Partial<RrcCompanyProfile>
    >({
      query: (arg) => ({
        url: '/compliance/rrc/companies',
        method: 'PUT',
        body: arg,
      }),
      invalidatesTags: (result, error, args) => {
        if (result) {
          return [
            { type: 'RrcCompanyProfiles', id: 'LIST' },
            { type: 'KeyRrcCompanyProfiles', id: result.profileKey },
            { type: 'ObjectRrcCompanyProfiles', id: result.companyId },
            { type: 'CurrentRrcCompanyProfile' },
          ];
        } else {
          return [
            { type: 'RrcCompanyProfiles', id: 'LIST' },
            { type: 'KeyRrcCompanyProfiles' },
            { type: 'ObjectRrcCompanyProfiles' },
            { type: 'CurrentRrcCompanyProfile' },
          ];
        }
      },
    }),
    deleteRrcCompanyProfile: builder.mutation<void, Partial<RrcCompanyProfile>>(
      {
        query: (arg) => ({
          url: '/compliance/rrc/companies',
          method: 'DELETE',
          body: arg,
        }),
        invalidatesTags: (result, error, args) => {
          return [
            { type: 'RrcCompanyProfiles', id: 'LIST' },
            { type: 'KeyRrcCompanyProfiles' },
            { type: 'ObjectRrcCompanyProfiles' },
            { type: 'CurrentRrcCompanyProfile' },
          ];
        },
      }
    ),
  }),
});

export const {
  useGetCurrentRrcCompanyProfilesByCompanyQuery,
  useGetRrcCompanyProfilesByCompanyQuery,
  useGetRrcCompanyProfilesByKeyQuery,
  useGetRrcCompanyProfileQuery,
  useGetCurrentRrcCompanyProfileByKeyQuery,
  useCreateRrcCompanyProfileMutation,
  useUpdateRrcCompanyProfileMutation,
  useDeleteRrcCompanyProfileMutation,
} = rrcCompanyProfileApi;
